// COMISIONES POR ANALISTA.
//
// Lissette, 2026-10-11: *"los analistas reciben comisiones diferentes a las que
// están en comisiones por póliza; me gustaría un reporte de analista, de cuando
// se le paga, según como el agente pone sus condiciones: si es por nivel, si
// vendió más de tanto"*. Confirmado con ella:
//
//   1. La comisión la pone la agente: una BASE y luego las reglas.
//   2. Las reglas van por NÚMERO DE PÓLIZAS vendidas.
//   3. La base es una cantidad FIJA por póliza; cambia según los niveles que
//      ponga la agente según el número de ventas.
//
// Ejemplo: Victor tiene base $25 por póliza y un nivel "desde 10 pólizas, $35".
// En octubre vende 12 → las 12 se pagan a $35 = $420. Si vende 8 → 8 × $25.
//
// El nivel que se alcanza en el mes se aplica a TODAS las pólizas de ese mes
// (no solo a las que pasan del umbral). Es como lo dijo ("se cambiará
// dependiendo del número de ventas") y es lo que se entiende al leer la regla.
//
// El mes de una venta es su FECHA DE VENTA (`saleDate`), la misma que usa el
// Reporte de Ventas; el analista es `analystName`, el mismo de ese reporte.
//
// Vive suelto, sin React: es dinero y se prueba por lo que devuelve.
import { numero, personasAseguradas } from './comisionPorPersona.js';
import { primaElegida } from './primaDePoliza.js';

const redondear = (n) => Math.round(n * 100) / 100;

/** El nombre del analista tal como se compara (sin espacios de más). */
export const claveDeAnalista = (nombre) => String(nombre ?? '').trim();

// POR TIPO DE PÓLIZA (Lissette 2026-10-29: *"quiero que las comisiones y las
// reglas sean por tipo de póliza; cada tipo de póliza tiene un pago diferente
// de comisión"*). Además de las reglas de cada analista hay una TABLA GENERAL
// (clave GENERAL) que vale para todos los analistas. Cada regla —la general o
// la de un analista— tiene su base y niveles "para cualquier tipo" (la forma
// de antes) y, si quiere, `porTipo: { [tipo]: { base, niveles } }`.
//
// Qué regla paga una póliza (gana la primera que exista):
//   1. la del analista para ESE tipo,
//   2. la del analista para cualquier tipo (las reglas de antes siguen igual),
//   3. la general para ESE tipo,
//   4. la general para cualquier tipo.
// "Existe" = tiene base mayor que 0 o algún nivel.
//
// Los niveles se cuentan por MES y por REGLA: con la tabla general por tipo,
// 10 pólizas de Salud suben el pago de Salud y las de Vida cuentan aparte.
// Con una regla "cualquier tipo", cuentan todas las que paga esa regla (como
// antes). Y se cuentan sobre TODAS las ventas del mes, no sobre lo que filtre
// la pantalla: un filtro no puede cambiar lo que se le paga a nadie.
//
// `usuarioId` en la regla de un analista lo vincula con su usuario de Deskte,
// por si el nombre escrito en la póliza no coincide con el del usuario.
export const GENERAL = '__general__';

/** Un tipo de póliza tal como se guarda (los alias viejos de Salud, a 'aca'). */
const ALIAS_DE_TIPO = { health: 'aca', medicare: 'aca' };
export const claveDeTipo = (t) => {
  const k = String(t ?? '').trim();
  return ALIAS_DE_TIPO[k] || k;
};

/** Para comparar el nombre de la póliza con el del usuario: sin acentos, mayúsculas ni espacios de más. */
export const normalizarNombre = (n) => String(n ?? '')
  .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
  .toLowerCase().replace(/\s+/g, ' ').trim();

// 2026-10-30 (Lissette): *"agrégale tipo de compañía: si no se especifica la
// compañía, se pone para todo ese tipo ese precio; si se especifica, esa
// compañía tiene ese precio"*, *"agrega la fuente de lead: si es personal se
// paga un precio y si es un lead de Facebook, otro"*, *"que las personas puedan
// personalizar sus propias reglas: sumar, porcentaje o multiplicar"* y *"por
// default un miembro, pero si la póliza tiene más miembros, que lo multiplique"*.
//
// Así queda cada regla (la general o la de un analista):
//   - `lineas`: precios especiales con condiciones — tipo, compañía y/o fuente
//     del lead (lo que se deje vacío vale para todo). Gana la línea que más
//     condiciones cumple; a igualdad, la compañía pesa más que la fuente y la
//     fuente más que el tipo.
//   - `porTipo`: el precio de cada tipo (como antes).
//   - base/niveles: cualquier otro tipo (como antes).
//   - `porMiembro` en cualquiera de ellas: lo que paga se multiplica por las
//     personas aseguradas de la póliza (1 si no tiene ninguna anotada). Los
//     niveles se siguen contando por PÓLIZAS (confirmado con ella).
//   - `ajustes`: sumar $, sumar %, multiplicar, restar $ o restar % al pago de
//     la póliza cuando se cumplen sus condiciones. Se aplican todos los que
//     coinciden, en orden: primero los de la tabla general y luego los del
//     analista. Nunca dejan el pago por debajo de $0.
//
// 2026-11-01 (Lissette): *"en ajustes propios también la opción de calcular la
// comisión sacándole el porcentaje a la prima, ya sea mensual o anualizada"* y
// *"una master list de reglas: regla 1, regla 2, y en esa master asignar a los
// seat; no quites la opción de hacerla individual"*.
//   - Ajustes `pctPrima` (el pago PASA a ser el % de la prima) y `sumarPctPrima`
//     (se SUMA el % de la prima), con `prima: 'mensual' | 'anual'`. La prima es
//     la de la ficha (`primaElegida`); la anual es la mensual × 12 si la ficha
//     solo tiene la mensual. Sin prima, ese % da $0.
//   - `plantillas` (solo en la tabla general): reglas guardadas con nombre y
//     su lista de `analistas`. Orden: lo individual del analista → su regla
//     guardada → la tabla general. Los ajustes: general → guardada → analista.
//     Si un analista está en dos, vale la primera.
export const OPERACIONES = ['sumar', 'sumarPct', 'multiplicar', 'restar', 'restarPct', 'pctPrima', 'sumarPctPrima'];
const OPERACIONES_DE_PRIMA = new Set(['pctPrima', 'sumarPctPrima']);
const texto = (v, max) => String(v ?? '').trim().slice(0, max);
/** La fuente del lead tal como se compara (el id del catálogo). */
export const claveDeFuente = (f) => texto(f, 60).toLowerCase();

function limpiarParte(r) {
  const base = Math.max(0, numero(r?.base));
  const porDesde = new Map();
  for (const n of Array.isArray(r?.niveles) ? r.niveles.slice(0, 20) : []) {
    const desde = Math.trunc(numero(n?.desde));
    const monto = numero(n?.monto);
    if (desde >= 1 && monto >= 0) porDesde.set(desde, redondear(monto));
  }
  const niveles = [...porDesde.entries()].sort((a, b) => a[0] - b[0]).map(([desde, monto]) => ({ desde, monto }));
  const parte = { base: redondear(base), niveles };
  if (r?.porMiembro === true) parte.porMiembro = true;
  return parte;
}
const puesta = (r) => !!r && (numero(r.base) > 0 || (Array.isArray(r.niveles) && r.niveles.length > 0));

/** Las condiciones de una línea o un ajuste: tipo, compañía y fuente ('' = cualquiera). */
function limpiarCondiciones(c) {
  return {
    tipo: claveDeTipo(texto(c?.tipo, 40)),
    compania: texto(c?.compania, 120),
    fuente: claveDeFuente(c?.fuente),
  };
}

function limpiarAjuste(a) {
  if (!a || typeof a !== 'object') return null;
  const operacion = OPERACIONES.includes(a.operacion) ? a.operacion : null;
  const valor = redondear(numero(a.valor));
  if (!operacion || !(valor > 0) || valor > 100000) return null;
  const ajuste = { ...limpiarCondiciones(a), operacion, valor };
  if (OPERACIONES_DE_PRIMA.has(operacion)) ajuste.prima = a.prima === 'anual' ? 'anual' : 'mensual';
  return ajuste;
}

/** La prima de la ficha en mensual y anual (la anual = mensual × 12 si solo hay una). */
export function primasDe(p) {
  const { monto, periodo } = primaElegida(p);
  const meses = periodo === 'annual' ? 12 : periodo === 'semiannual' ? 6 : 1;
  const mensual = monto / meses;
  return { mensual: redondear(mensual), anual: redondear(mensual * 12) };
}

/** ¿La póliza cumple estas condiciones? Lo vacío vale para todo. */
function cumple(cond, poliza) {
  if (cond.tipo && cond.tipo !== poliza.tipo) return false;
  if (cond.compania && normalizarNombre(cond.compania) !== poliza.compania) return false;
  if (cond.fuente && cond.fuente !== poliza.fuente) return false;
  return true;
}
const peso = (c) => (c.compania ? 4 : 0) + (c.fuente ? 2 : 0) + (c.tipo ? 1 : 0);

/** Lo que de una póliza miran las reglas: su tipo, su compañía y la fuente del lead. */
export function datosParaReglas(p) {
  return { tipo: claveDeTipo(p?.type), compania: normalizarNombre(p?.carrier), fuente: claveDeFuente(p?.leadType) };
}

/** Personas aseguradas de la póliza para pagar por miembro: 1 si no tiene ninguna anotada. */
export const miembrosDe = (p) => Math.max(1, personasAseguradas(p));

/**
 * Las reglas guardadas, limpias:
 *   { [analista | GENERAL]: { base, niveles: [{ desde, monto }], porMiembro?,
 *       porTipo?: { [tipo]: { base, niveles, porMiembro? } },
 *       lineas?: [{ tipo, compania, fuente, base, niveles, porMiembro? }],
 *       ajustes?: [{ tipo, compania, fuente, operacion, valor }],
 *       usuarioId? } }
 * Niveles ordenados de menor a mayor, sin repetir el mismo "desde" y sin
 * importes ni cantidades que no sean números positivos. `porTipo`, `lineas`,
 * `ajustes` y `usuarioId` solo aparecen si traen algo.
 */
export function normalizarReglas(raw) {
  const fuera = {};
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return fuera;
  for (const [nombre, r] of Object.entries(raw).slice(0, 200)) {
    const clave = claveDeAnalista(nombre).slice(0, 120);
    if (!clave || !r || typeof r !== 'object' || Array.isArray(r)) continue;
    const regla = limpiarRegla(r);
    const usuarioId = typeof r.usuarioId === 'string' ? r.usuarioId.trim().slice(0, 64) : '';
    if (usuarioId && clave !== GENERAL) regla.usuarioId = usuarioId;
    if (clave === GENERAL) {
      const plantillas = limpiarPlantillas(r.plantillas);
      if (plantillas.length) regla.plantillas = plantillas;
    }
    fuera[clave] = regla;
  }
  return fuera;
}

/** Una regla (de un analista, la general o una guardada) sin el vínculo ni las guardadas. */
function limpiarRegla(r) {
  const regla = limpiarParte(r);
  const porTipo = {};
  if (r.porTipo && typeof r.porTipo === 'object' && !Array.isArray(r.porTipo)) {
    for (const [tipo, rt] of Object.entries(r.porTipo).slice(0, 80)) {
      const t = claveDeTipo(tipo).slice(0, 40);
      if (!t || !rt || typeof rt !== 'object') continue;
      const parte = limpiarParte(rt);
      if (puesta(parte)) porTipo[t] = parte;
    }
  }
  if (Object.keys(porTipo).length) regla.porTipo = porTipo;
  const lineas = [];
  for (const l of Array.isArray(r.lineas) ? r.lineas.slice(0, 100) : []) {
    if (!l || typeof l !== 'object') continue;
    const cond = limpiarCondiciones(l);
    const parte = limpiarParte(l);
    // Una línea sin condiciones es "cualquier otro tipo", que ya existe.
    if ((cond.tipo || cond.compania || cond.fuente) && puesta(parte)) lineas.push({ ...cond, ...parte });
  }
  if (lineas.length) regla.lineas = lineas;
  const ajustes = (Array.isArray(r.ajustes) ? r.ajustes.slice(0, 50) : []).map(limpiarAjuste).filter(Boolean);
  if (ajustes.length) regla.ajustes = ajustes;
  return regla;
}

/** Las reglas guardadas: [{ id, nombre, analistas, ...regla }]. Sin nombre o id no valen. */
function limpiarPlantillas(lista) {
  const fuera = [];
  const ids = new Set();
  for (const pl of Array.isArray(lista) ? lista.slice(0, 50) : []) {
    if (!pl || typeof pl !== 'object' || Array.isArray(pl)) continue;
    const id = texto(pl.id, 40).replace(/[^A-Za-z0-9_-]/g, '');
    const nombre = texto(pl.nombre, 80);
    if (!id || !nombre || ids.has(id)) continue;
    ids.add(id);
    const vistos = new Set();
    const analistas = (Array.isArray(pl.analistas) ? pl.analistas.slice(0, 200) : [])
      .map((a) => claveDeAnalista(a).slice(0, 120))
      .filter((a) => a && !vistos.has(normalizarNombre(a)) && vistos.add(normalizarNombre(a)));
    fuera.push({ id, nombre, analistas, ...limpiarRegla(pl) });
  }
  return fuera;
}

/** La regla guardada a la que está asignado este analista, o null (la primera si está en varias). */
export function plantillaDe(reglas, analista) {
  const n = normalizarNombre(analista);
  if (!n) return null;
  return (reglas[GENERAL]?.plantillas || []).find((pl) => pl.analistas.some((a) => normalizarNombre(a) === n)) || null;
}

/** La línea especial de esta regla que mejor encaja con la póliza, o null. */
function mejorLinea(regla, poliza) {
  let mejor = null;
  (regla?.lineas || []).forEach((l, i) => {
    if (!cumple(l, poliza)) return;
    if (!mejor || peso(l) > peso(mejor.l)) mejor = { l, i };
  });
  return mejor;
}

/**
 * La regla que paga una póliza de este analista, o null. `datos` trae la
 * compañía y la fuente del lead (`datosParaReglas`); sin ellos solo se mira el
 * tipo, como antes. `clave` identifica la regla, para contar los niveles por
 * separado.
 *
 * Orden: lo del analista (línea especial → su tipo → cualquier otro tipo) y,
 * si no tiene nada que sirva, lo mismo en la tabla general.
 */
export function reglaParaPoliza(reglas, analista, tipo, datos = {}) {
  const t = claveDeTipo(tipo);
  const poliza = { tipo: t, compania: datos.compania || '', fuente: datos.fuente || '' };
  const pl = plantillaDe(reglas, analista);
  const cadena = [['analista', reglas[analista], ''], ['plantilla', pl, pl ? `plantilla:${pl.id}` : ''], ['general', reglas[GENERAL], '']];
  for (const [quien, r, id] of cadena) {
    if (!r) continue;
    const k = id || quien;
    const extra = quien === 'plantilla' ? { nombre: pl.nombre } : {};
    const linea = mejorLinea(r, poliza);
    if (linea) return { regla: linea.l, clave: `${k}|linea:${linea.i}`, origen: `${quien}-especial`, ...extra };
    if (r.porTipo?.[t]) return { regla: r.porTipo[t], clave: `${k}|tipo:${t}`, origen: `${quien}-tipo`, ...extra };
    if (puesta(r)) return { regla: r, clave: `${k}|todos`, origen: quien, ...extra };
  }
  return null;
}

/** Los ajustes que tocan a esta póliza: generales, luego los de su regla guardada y luego los del analista. */
export function ajustesParaPoliza(reglas, analista, datos) {
  return [
    ...(reglas[GENERAL]?.ajustes || []),
    ...(plantillaDe(reglas, analista)?.ajustes || []),
    ...(reglas[analista]?.ajustes || []),
  ].filter((a) => cumple(a, datos));
}

/** Aplica los ajustes en orden. Nunca baja de $0. `primas` = { mensual, anual } de la póliza. */
export function aplicarAjustes(monto, ajustes, primas = { mensual: 0, anual: 0 }) {
  let m = numero(monto);
  for (const a of ajustes || []) {
    const v = numero(a.valor);
    const prima = numero(a.prima === 'anual' ? primas.anual : primas.mensual);
    if (a.operacion === 'pctPrima') m = (prima * v) / 100;
    else if (a.operacion === 'sumarPctPrima') m += (prima * v) / 100;
    else if (a.operacion === 'sumar') m += v;
    else if (a.operacion === 'sumarPct') m += (m * v) / 100;
    else if (a.operacion === 'multiplicar') m *= v;
    else if (a.operacion === 'restar') m -= v;
    else if (a.operacion === 'restarPct') m -= (m * v) / 100;
    m = Math.max(0, m);
  }
  return redondear(m);
}

/** ¿Este analista (el nombre de la póliza) es este usuario? Por el vínculo de la regla o por el nombre. */
export function esDelUsuario(analista, usuario, reglas = {}) {
  if (!usuario) return false;
  const vinculo = reglas[claveDeAnalista(analista)]?.usuarioId;
  if (vinculo) return vinculo === usuario.id;
  const a = normalizarNombre(analista);
  return !!a && (a === normalizarNombre(usuario.name) || a === normalizarNombre(usuario.displayName));
}

/**
 * Cuánto se paga POR PÓLIZA con estas ventas en el mes, y qué nivel es.
 * `nivel` = null cuando se queda en la base.
 */
export function montoPorPoliza(regla, ventas) {
  if (!regla) return { monto: 0, nivel: null };
  let monto = numero(regla.base);
  let nivel = null;
  for (const n of regla.niveles || []) {
    if (ventas >= n.desde) { monto = numero(n.monto); nivel = n; }
  }
  return { monto, nivel };
}

/** "YYYY-MM" de una fecha `YYYY-MM-DD` sin pasar por Date (que correría el día). */
export function mesDe(fecha) {
  const m = /^(\d{4})-(\d{2})/.exec(String(fecha ?? ''));
  return m ? `${m[1]}-${m[2]}` : '';
}

/**
 * Lo que se le paga por cada póliza con analista y fecha de venta, contando los
 * niveles sobre TODAS las ventas del mes (no sobre un filtro) y por PÓLIZAS.
 *   { [polizaId]: { analista, mes, tipo, monto, nivel, origen, unitario, miembros, ajustes } }
 * `unitario` = lo de la regla por póliza (o por miembro), `miembros` = por
 * cuántos se multiplicó (1 si la regla no es por miembro), `ajustes` = los que
 * se aplicaron. `monto` es lo que se paga al final. origen null = sin regla.
 */
export function pagosPorPoliza(polizas, reglasCrudas) {
  const reglas = normalizarReglas(reglasCrudas);
  const grupos = new Map();
  const info = [];
  for (const p of Array.isArray(polizas) ? polizas : []) {
    const analista = claveDeAnalista(p?.analystName);
    const mes = mesDe(p?.saleDate);
    if (!analista || !mes) continue;
    const datos = datosParaReglas(p);
    const r = reglaParaPoliza(reglas, analista, datos.tipo, datos);
    const grupo = `${analista}\u0000${mes}\u0000${r ? r.clave : 'sin'}`;
    grupos.set(grupo, (grupos.get(grupo) || 0) + 1);
    info.push({ p, analista, mes, datos, r, grupo });
  }
  const pagos = {};
  for (const { p, analista, mes, datos, r, grupo } of info) {
    if (!r) {
      // Sin precio puesto, un ajuste "Pagar % de la prima" ES la comisión
      // (la agente puede pagar solo por porcentaje). Los demás ajustes no
      // inventan un pago donde no hay regla.
      const ajustes = ajustesParaPoliza(reglas, analista, datos);
      if (ajustes.some((a) => a.operacion === 'pctPrima')) {
        const monto = aplicarAjustes(0, ajustes, primasDe(p));
        pagos[p.id] = { analista, mes, tipo: datos.tipo, monto, nivel: null, origen: 'prima', unitario: 0, miembros: 1, ajustes };
      } else {
        pagos[p.id] = { analista, mes, tipo: datos.tipo, monto: 0, nivel: null, origen: null, unitario: 0, miembros: 1, ajustes: [] };
      }
      continue;
    }
    const { monto: unitario, nivel } = montoPorPoliza(r.regla, grupos.get(grupo));
    const miembros = r.regla.porMiembro ? miembrosDe(p) : 1;
    const ajustes = ajustesParaPoliza(reglas, analista, datos);
    const monto = aplicarAjustes(redondear(unitario * miembros), ajustes, primasDe(p));
    pagos[p.id] = { analista, mes, tipo: datos.tipo, monto, nivel, origen: r.origen, unitario, miembros, ajustes, ...(r.nombre ? { regla: r.nombre } : {}) };
  }
  return pagos;
}

/**
 * El reporte entre dos fechas de venta (YYYY-MM-DD, las dos incluidas): una fila
 * por analista que vendió en ese rango o que tiene reglas puestas.
 *
 *   { analista, ventas, polizas, monto, nivel, total, conReglas, porTipo, sinRegla }
 *
 * `monto`/`nivel` son los de la fila cuando todas sus pólizas se pagan igual
 * (null si no). `porTipo` = [{ tipo, ventas, monto, nivel, total }].
 * `filtro(p)` deja fuera pólizas de la vista (tipo, compañía…) SIN cambiar lo
 * que cuesta cada una. Una póliza sin analista o sin fecha de venta no entra:
 * se cuentan aparte en `sinAnalista` / `sinFecha` para decirlo en pantalla.
 */
export function reporteDeAnalistasEntre(polizas, reglasCrudas, { desde = '', hasta = '', filtro = null, soloAnalistas = null } = {}) {
  const reglas = normalizarReglas(reglasCrudas);
  const pagos = pagosPorPoliza(polizas, reglas);
  const dentro = (f) => (!desde || f >= desde) && (!hasta || f <= hasta);
  const porAnalista = new Map();
  let sinAnalista = 0;
  let sinFecha = 0;
  for (const p of Array.isArray(polizas) ? polizas : []) {
    if (filtro && !filtro(p)) continue;
    const analista = claveDeAnalista(p?.analystName);
    const fecha = String(p?.saleDate ?? '').slice(0, 10);
    if (!mesDe(fecha)) { if (analista) sinFecha += 1; continue; }
    if (!dentro(fecha)) continue;
    if (!analista) { sinAnalista += 1; continue; }
    if (soloAnalistas && !soloAnalistas(analista)) continue;
    if (!porAnalista.has(analista)) porAnalista.set(analista, []);
    porAnalista.get(analista).push(p);
  }
  const conRegla = [...Object.keys(reglas), ...(reglas[GENERAL]?.plantillas || []).flatMap((pl) => pl.analistas)];
  for (const nombre of conRegla) {
    if (nombre === GENERAL || porAnalista.has(nombre)) continue;
    if (soloAnalistas && !soloAnalistas(nombre)) continue;
    porAnalista.set(nombre, []);
  }
  const filas = [...porAnalista.entries()].map(([analista, ps]) => {
    const tipos = new Map();
    let total = 0;
    let sinRegla = 0;
    const montos = new Set();
    const niveles = new Set();
    for (const p of ps) {
      const pago = pagos[p.id] || { monto: 0, nivel: null, origen: null, tipo: claveDeTipo(p.type) };
      total += pago.monto;
      if (!pago.origen) sinRegla += 1;
      montos.add(pago.monto);
      niveles.add(pago.nivel ? `${pago.nivel.desde}:${pago.nivel.monto}` : '');
      const t = tipos.get(pago.tipo) || { tipo: pago.tipo, ventas: 0, total: 0, montos: new Set(), nivel: null };
      t.ventas += 1; t.total += pago.monto; t.montos.add(pago.monto); if (pago.nivel) t.nivel = pago.nivel;
      tipos.set(pago.tipo, t);
    }
    const unico = ps.length > 0 && montos.size === 1;
    const primero = ps.length ? pagos[ps[0].id] : null;
    const reglaSola = ps.length === 0 ? reglaParaPoliza(reglas, analista, '') : null;
    return {
      analista,
      ventas: ps.length,
      polizas: ps,
      monto: unico ? [...montos][0] : (ps.length === 0 && reglaSola ? montoPorPoliza(reglaSola.regla, 0).monto : (ps.length === 0 ? 0 : null)),
      nivel: unico && niveles.size === 1 ? (primero?.nivel || null) : null,
      total: redondear(total),
      conReglas: !!reglas[analista] || !!plantillaDe(reglas, analista) || ps.some((p) => pagos[p.id]?.origen),
      sinRegla,
      porTipo: [...tipos.values()]
        .map((t) => ({ tipo: t.tipo, ventas: t.ventas, total: redondear(t.total), monto: t.montos.size === 1 ? [...t.montos][0] : null, nivel: t.nivel }))
        .sort((a, b) => b.total - a.total || b.ventas - a.ventas),
    };
  }).sort((a, b) => b.total - a.total || b.ventas - a.ventas || a.analista.localeCompare(b.analista));
  return {
    filas,
    pagos,
    total: redondear(filas.reduce((s, f) => s + f.total, 0)),
    ventas: filas.reduce((s, f) => s + f.ventas, 0),
    sinAnalista,
    sinFecha,
  };
}

/** El reporte de un mes ("YYYY-MM"): el de antes, sobre el reporte por fechas. */
export function reporteDeAnalistas(polizas, reglasCrudas, mes) {
  return reporteDeAnalistasEntre(polizas, reglasCrudas, { desde: `${mes}-01`, hasta: `${mes}-31` });
}
